"use client";

import { useState, useEffect } from "react";
import Image from "next/image";

interface SuperAdminSecurityModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
  onSuccess: () => void;
}

export default function SuperAdminSecurityModal({ 
  isOpen, 
  onClose, 
  email, 
  onSuccess 
}: SuperAdminSecurityModalProps) {
  const [currentStep, setCurrentStep] = useState<'faceId' | 'twoFactor'>('faceId');
  const [faceIdStatus, setFaceIdStatus] = useState<'idle' | 'scanning' | 'success' | 'failed'>('idle');
  const [twoFactorCode, setTwoFactorCode] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  // Detect mobile screen
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Resend countdown timer
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  // Start countdown when modal opens and we're on two factor step
  useEffect(() => {
    if (isOpen && currentStep === 'twoFactor') {
      setResendCountdown(60);
    }
  }, [isOpen, currentStep]);

  if (!isOpen) return null;

  const handleFaceIdScan = async () => {
    setFaceIdStatus('scanning');
    setError(null);
    
    try {
      // Simulate FaceID scanning
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Simulate success (you can change this to test failure)
      const isSuccess = Math.random() > 0.3; // 70% success rate for demo
      
      if (isSuccess) {
        setFaceIdStatus('success');
        setTimeout(() => {
          setCurrentStep('twoFactor');
        }, 1500);
      } else {
        setFaceIdStatus('failed');
        setError("Face ID verification failed. Please try again.");
        setTimeout(() => {
          setFaceIdStatus('idle');
        }, 2000);
      }
    } catch (err) {
      setFaceIdStatus('failed');
      setError("Face ID verification failed. Please try again.");
      setTimeout(() => {
        setFaceIdStatus('idle');
      }, 2000);
    }
  };

  const handleTwoFactorInput = (index: number, value: string) => {
    if (value.length > 1) return;
    
    const newCode = [...twoFactorCode];
    newCode[index] = value;
    setTwoFactorCode(newCode);
    setError(null);

    // Auto-verify when all digits are entered
    if (newCode.every(digit => digit !== "")) {
      handleTwoFactorVerify(newCode);
    }
  };

  const handleTwoFactorVerify = async (codeToVerify: string[] = twoFactorCode) => {
    const verificationCode = codeToVerify.join('');
    
    if (verificationCode.length !== 6) {
      setError("Please enter the complete 6-digit code");
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Accept "123456" as valid for demo
      if (verificationCode === "123456") {
        console.log("Super Admin security verification successful");
        onSuccess();
        onClose();
      } else {
        setError("Invalid authentication code. Please try again.");
        setTwoFactorCode(["", "", "", "", "", ""]);
      }
    } catch (err) {
      setError("Verification failed. Please try again.");
      setTwoFactorCode(["", "", "", "", "", ""]);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCountdown > 0) return;
    
    setError(null);
    setResendCountdown(60);
    console.log("Resending 2FA code to:", email);
  };

  const renderFaceIdStep = () => (
    <div className={`relative ${isMobile ? 'px-6 py-8' : 'px-8 py-10'}`}>
      {/* Error message */}
      {error && (
        <div className="absolute top-0 left-0 right-0 flex justify-center">
          <div className="bg-red-900/30 border border-red-500/50 rounded-full px-4 py-2 flex items-center space-x-2 shadow-lg translate-y-[-50%] max-w-[90%] backdrop-blur-sm">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 4V9" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 11.5V12" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="text-red-400 text-sm font-medium">{error}</span>
          </div>
        </div>
      )}
      
      {/* Logo with glow effect */}
      <div className={`relative ${isMobile ? 'mb-6' : 'mb-8'}`}>
        <div className="absolute inset-0 bg-gradient-to-r from-[#CB6CE6] to-[#6B21A8] rounded-lg blur-xl opacity-20 w-12 h-12"></div>
        <div className="relative">
          <Image
            src="/phoebe-logo.png"
            alt="Phoebe Logo"
            width={40}
            height={40}
            className="object-contain relative z-10 drop-shadow-lg"
          />
        </div>
      </div>

      {/* Title */}
      <div className={isMobile ? 'mb-8' : 'mb-10'}>
        <h2 className={`font-black text-transparent bg-clip-text bg-gradient-to-r from-[#CB6CE6] via-white to-[#6B21A8] mb-3 ${
          isMobile ? 'text-2xl leading-tight' : 'text-3xl leading-tight'
        }`}>
          Super Admin Security
        </h2>
        
        <p className={`text-[#9CA3AF] leading-relaxed mb-4 ${
          isMobile ? 'text-sm' : 'text-base'
        }`}>
          Additional verification required for Super Admin access
        </p>
        
        <div className="bg-black/20 rounded-lg p-3 border border-[#CB6CE6]/20">
          <div className="text-white font-medium text-sm">{email}</div>
          <div className="text-[#CB6CE6] text-xs mt-1">Role: Super Admin</div>
        </div>
      </div>

      {/* FaceID Section */}
      <div className="text-center mb-8">
        <div className="mb-6">
          <h3 className="text-white font-bold text-lg mb-2">Step 1: Face ID Verification</h3>
          <p className="text-[#9CA3AF] text-sm">Position your face within the frame</p>
        </div>

        {/* FaceID Scanner */}
        <div className="relative mx-auto mb-6" style={{ width: '200px', height: '200px' }}>
          {/* Scanning Frame */}
          <div className={`absolute inset-0 rounded-full border-4 transition-all duration-500 ${
            faceIdStatus === 'idle' ? 'border-[#CB6CE6]/30' :
            faceIdStatus === 'scanning' ? 'border-[#CB6CE6] animate-pulse' :
            faceIdStatus === 'success' ? 'border-green-500' :
            'border-red-500'
          }`}>
            {/* Scanner Animation */}
            {faceIdStatus === 'scanning' && (
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#CB6CE6]/20 to-[#6B21A8]/20 animate-ping"></div>
            )}
          </div>

          {/* Face Icon */}
          <div className="absolute inset-0 flex items-center justify-center">
            {faceIdStatus === 'success' ? (
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : faceIdStatus === 'failed' ? (
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 9L9 15M9 9L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 21V19C20 16.7909 18.2091 15 16 15H8C5.79086 15 4 16.7909 4 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke={faceIdStatus === 'scanning' ? '#CB6CE6' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </div>
        </div>

        {/* Status Text */}
        <div className="mb-6">
          {faceIdStatus === 'idle' && (
            <p className="text-[#9CA3AF] text-sm">Ready to scan</p>
          )}
          {faceIdStatus === 'scanning' && (
            <p className="text-[#CB6CE6] text-sm animate-pulse">Scanning your face...</p>
          )}
          {faceIdStatus === 'success' && (
            <p className="text-green-500 text-sm">Face ID verified successfully!</p>
          )}
          {faceIdStatus === 'failed' && (
            <p className="text-red-500 text-sm">Verification failed</p>
          )}
        </div>

        {/* Scan Button */}
        {(faceIdStatus === 'idle' || faceIdStatus === 'failed') && (
          <button
            onClick={handleFaceIdScan}
            className="bg-gradient-to-r from-[#CB6CE6] to-[#6B21A8] text-white px-8 py-3 rounded-xl font-bold text-lg hover:shadow-2xl hover:shadow-[#CB6CE6]/30 transform hover:scale-[1.02] transition-all duration-500 relative overflow-hidden group"
          >
            <span className="relative z-10">Start Face ID Scan</span>
            <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-1000 ease-out"></div>
          </button>
        )}
      </div>
    </div>
  );

  const renderTwoFactorStep = () => (
    <div className={`relative ${isMobile ? 'px-6 py-8' : 'px-8 py-10'}`}>
      {/* Error message */}
      {error && (
        <div className="absolute top-0 left-0 right-0 flex justify-center">
          <div className="bg-red-900/30 border border-red-500/50 rounded-full px-4 py-2 flex items-center space-x-2 shadow-lg translate-y-[-50%] max-w-[90%] backdrop-blur-sm">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 4V9" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 11.5V12" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8 C2 11.3137 4.68629 14 8 14Z" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="text-red-400 text-sm font-medium">{error}</span>
          </div>
        </div>
      )}
      
      {/* Logo with glow effect */}
      <div className={`relative ${isMobile ? 'mb-6' : 'mb-8'}`}>
        <div className="absolute inset-0 bg-gradient-to-r from-[#CB6CE6] to-[#6B21A8] rounded-lg blur-xl opacity-20 w-12 h-12"></div>
        <div className="relative">
          <Image
            src="/phoebe-logo.png"
            alt="Phoebe Logo"
            width={40}
            height={40}
            className="object-contain relative z-10 drop-shadow-lg"
          />
        </div>
      </div>

      {/* Title */}
      <div className={isMobile ? 'mb-8' : 'mb-10'}>
        <h2 className={`font-black text-transparent bg-clip-text bg-gradient-to-r from-[#CB6CE6] via-white to-[#6B21A8] mb-3 ${
          isMobile ? 'text-2xl leading-tight' : 'text-3xl leading-tight'
        }`}>
          Two-Factor Authentication
        </h2>
        
        <p className={`text-[#9CA3AF] leading-relaxed mb-4 ${
          isMobile ? 'text-sm' : 'text-base'
        }`}>
          Step 2: Enter the 6-digit code from your authenticator app
        </p>
        
        <div className="bg-black/20 rounded-lg p-3 border border-[#CB6CE6]/20">
          <div className="flex items-center space-x-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="text-green-400 text-sm font-medium">Face ID Verified</span>
          </div>
        </div>
      </div>

      {/* Two Factor Code Input */}
      <div className="mb-8">
        <label className="block text-white font-medium mb-4 text-sm">
          Authentication Code
        </label>
        
        <div className="flex justify-center space-x-3 mb-6">
          {twoFactorCode.map((digit, index) => (
            <input
              key={index}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              value={digit}
              onChange={(e) => handleTwoFactorInput(index, e.target.value.replace(/\D/g, ''))}
              className={`w-12 h-14 text-center text-xl font-bold bg-black/30 border border-[#CB6CE6]/30 rounded-xl text-white hover:border-[#CB6CE6]/50 focus:border-[#CB6CE6] focus:outline-none transition-all duration-300 backdrop-blur-sm ${
                digit ? 'border-[#CB6CE6] bg-[#CB6CE6]/10' : ''
              }`}
              disabled={isVerifying}
            />
          ))}
        </div>

        {/* Resend Code */}
        <div className="text-center">
          <p className="text-[#9CA3AF] text-sm mb-2">
            Didn't receive the code?
          </p>
          <button
            onClick={handleResendCode}
            disabled={resendCountdown > 0}
            className={`text-sm font-medium transition-colors ${
              resendCountdown > 0
                ? 'text-[#9CA3AF] cursor-not-allowed'
                : 'text-[#CB6CE6] hover:text-[#6B21A8] cursor-pointer'
            }`}
          >
            {resendCountdown > 0 
              ? `Resend in ${resendCountdown}s` 
              : 'Resend Code'
            }
          </button>
        </div>
      </div>

      {/* Verify Button */}
      <button
        onClick={() => handleTwoFactorVerify()}
        disabled={isVerifying || twoFactorCode.some(digit => !digit)}
        className="w-full bg-gradient-to-r from-[#CB6CE6] to-[#6B21A8] text-white py-4 rounded-xl font-bold text-lg hover:shadow-2xl hover:shadow-[#CB6CE6]/30 transform hover:scale-[1.02] transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none relative overflow-hidden group"
      >
        {isVerifying ? (
          <div className="flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
            Verifying...
          </div>
        ) : (
          <span className="relative z-10">Complete Verification</span>
        )}
        
        <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-1000 ease-out"></div>
      </button>
      
      {/* Back button */}
      <div className="mt-6 text-center">
        <button 
          onClick={() => setCurrentStep('faceId')}
          className="text-[#9CA3AF] hover:text-white text-sm transition-colors"
          disabled={isVerifying}
        >
          ← Back to Face ID
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div className={`fixed z-50 pointer-events-none ${
        isMobile 
          ? 'inset-x-0 top-[77px] flex justify-center' 
          : 'left-1/2 top-[77px] transform -translate-x-1/2'
      }`}>
        <div 
          className={`bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f1419] rounded-2xl border border-[#CB6CE6]/20 pointer-events-auto relative shadow-2xl shadow-[#CB6CE6]/10 ${
            isMobile 
              ? 'w-[90%] max-h-[80vh] overflow-y-auto' 
              : 'w-[420px] min-h-[500px]'
          }`} 
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button 
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-all duration-300 z-10 group"
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L13 13M1 13L13 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="group-hover:stroke-[#CB6CE6] transition-colors"/>
            </svg>
          </button>
          
          {/* Step Progress Indicator */}
          <div className="absolute top-4 left-4 flex space-x-2">
            <div className={`w-3 h-3 rounded-full transition-colors ${
              currentStep === 'faceId' ? 'bg-[#CB6CE6]' : 'bg-green-500'
            }`}></div>
            <div className={`w-3 h-3 rounded-full transition-colors ${
              currentStep === 'twoFactor' ? 'bg-[#CB6CE6]' : 'bg-[#9CA3AF]'
            }`}></div>
          </div>
          
          {/* Render current step */}
          {currentStep === 'faceId' ? renderFaceIdStep() : renderTwoFactorStep()}
        </div>
      </div>
    </>
  );
}